import React, { useEffect, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom'; // Added for routing
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import ServicesSection from './components/ServicesSection';
import AfkPipelineSection from './components/AfkPipelineSection';
import WhyChooseUsSection from './components/WhyChooseUsSection';
import ProjectsSection from './components/ProjectsSection';
import TechnologiesSection from './components/TechnologiesSection';
import FaqSection, { faqPairs } from './components/FaqSection';
import CTASection from './components/CTASection';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer';
import ServiceModal from './components/ServiceModal';
import { Service } from './components/ServicesSection';
import BlogPage from './components/BlogPage'; // Placeholder for Blog page component
import BlogPostDetailsPage from './components/BlogPostDetailsPage'; // Added for single post view
import SuccessStoryPage from './components/SuccessStoryPage'; // Added for success stories
import AfkDevelopmentPage from './components/AfkDevelopmentPage';
import AboutPage from './components/AboutPage';
import ContactPage from './components/ContactPage';
import NotFoundPage from './components/NotFoundPage';
import Seo from './seo/Seo';
import { organizationLd, websiteLd, faqLd } from './seo/jsonLd';
import { CTA_LABEL, CTA_LABEL_AWS } from './site';

// Main CSS import (if not already in index.tsx, though index.tsx is preferred for global CSS)
// import './index.css'; 

// Component for the main landing page content
const HomePageLayout = ({ onOpenContactModal, onOpenServiceModal }: {
  onOpenContactModal: () => void;
  onOpenServiceModal: (service: Service) => void;
}) => (
  <>
    <Seo
      title="Kult.io | AI Agents, Agentic Workflows & AWS Cloud"
      description="We build AI agents and agentic workflows that use your tools and documents to finish real work. Cloud-agnostic, Claude-first. AWS infrastructure for 10+ years."
      path="/"
      jsonLd={[organizationLd, websiteLd, faqLd(faqPairs)]}
    />
    <HeroSection />
    <ServicesSection onOpenServiceModal={onOpenServiceModal} />
    <AfkPipelineSection />
    <WhyChooseUsSection /> 
    <ProjectsSection />
    <TechnologiesSection />
    <FaqSection />
    <CTASection onOpenContactModal={onOpenContactModal} />
  </>
);

// A new route starts at the top. The jump is instant rather than smooth: the route
// has already swapped, so a smooth scroll would animate the new page past the reader.
// The entrance below is what makes it feel like a transition.
//
// A hash is the exception, and it is the reason this owns the behaviour rather than the
// navbar: the footer links to /#why and /#aws, which no nav item knows about, and
// without this they would land at the top of the homepage.
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const target = hash ? document.getElementById(hash.slice(1)) : null;
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return null;
};

// The services carry their anchor as `#service-<slug>`; the slug doubles as the
// booking's utm_content, so a booking from a modal names the service it came from.
const placementForService = (service: Service) => service.link.replace('#service-', '');

// The two practices sell two different free assessments. Booking the agent one from a
// cloud service's dialog books the wrong meeting, and it is not an error the site can
// catch afterwards.
const bookingLabelForService = (service: Service) =>
  service.category === 'cloud' ? CTA_LABEL_AWS : CTA_LABEL;

function App() {
  const { pathname } = useLocation();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [contactSubject, setContactSubject] = useState<string | undefined>(undefined);
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  // The prerender's verify pass waits on this before reading hydration errors.
  useEffect(() => {
    window.__APP_READY__ = true;
  }, []);

  const openContactModal = () => {
    closeServiceModal();
    setContactSubject(undefined);
    setIsContactModalOpen(true);
  };
  // Separate entry point so a click handler's event object can never arrive as a subject.
  const openContactModalForService = (serviceTitle: string) => {
    closeServiceModal();
    setContactSubject(serviceTitle);
    setIsContactModalOpen(true);
  };
  const closeContactModal = () => setIsContactModalOpen(false);

  const openServiceModal = (service: Service) => {
    closeContactModal();
    setSelectedService(service);
  };
  const closeServiceModal = () => setSelectedService(null);

  return (
    <div className="min-h-screen bg-gray-900 text-white"> {/* Base background and text color */}
      <ScrollToTop />
      <Navbar />
      <main key={pathname} className="animate-route-in">
        <Routes>
          <Route 
            path="/"
            element={<HomePageLayout onOpenContactModal={openContactModal} onOpenServiceModal={openServiceModal} />}
          />
          <Route path="/afk-development" element={<AfkDevelopmentPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostDetailsPage />} /> {/* Added route for single blog post */}
          <Route
            path="/success-stories/:slug"
            element={<SuccessStoryPage onOpenContactModal={openContactModal} />}
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <Footer />
      <ContactSection isOpen={isContactModalOpen} onClose={closeContactModal} subject={contactSubject} />
      {selectedService && (
        <ServiceModal 
          isOpen={!!selectedService}
          onClose={closeServiceModal}
          onRequestContact={() => openContactModalForService(selectedService.title)}
          title={selectedService.title}
          longDescription={selectedService.longDescription || ''}
          placement={placementForService(selectedService)}
          bookingLabel={bookingLabelForService(selectedService)}
          imageUrl={selectedService.imageUrl}
        />
      )}
    </div>
  );
}

export default App;
